"use client";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function BookmarkButton({
    articleId,
    initialBookmarked,
    size = "default",
    showSeparator = false,
    onToggle,
}) {
    const { data: session } = useSession();
    const t = useTranslations();
    const [isBookmarked, setIsBookmarked] = useState(initialBookmarked ?? false);
    const [isLoading, setIsLoading] = useState(false);

    // Self-fetch mode: when initialBookmarked is not provided (e.g. article detail page)
    useEffect(() => {
        if (initialBookmarked !== undefined || !session || !articleId) return;

        const checkBookmark = async () => {
            try {
                const res = await fetch("/api/article/is-bookmarked", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ articleId }),
                });
                if (res.ok) {
                    const data = await res.json();
                    setIsBookmarked(data.isBookmarked);
                }
            } catch (error) {
                // Silently fail
            }
        };

        checkBookmark();
    }, [session, articleId, initialBookmarked]);

    // Update state when initialBookmarked prop changes (e.g. after bulk fetch completes)
    useEffect(() => {
        if (initialBookmarked !== undefined) {
            setIsBookmarked(initialBookmarked);
        }
    }, [initialBookmarked]);

    if (!session) return null;

    const toggleBookmark = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isLoading) return;

        setIsLoading(true);
        const previousState = isBookmarked;
        setIsBookmarked(!isBookmarked);

        try {
            const res = await fetch("/api/article/bookmark", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ articleId }),
            });

            if (res.ok) {
                const data = await res.json();
                setIsBookmarked(data.isBookmarked);
                if (onToggle) onToggle(data.isBookmarked);
            } else {
                setIsBookmarked(previousState);
            }
        } catch (error) {
            setIsBookmarked(previousState);
        } finally {
            setIsLoading(false);
        }
    };

    const sizeClasses = size === "small" ? "w-8 h-8" : "w-9 h-9";
    const iconSizeClass = size === "small" ? "text-[16px]" : "text-[18px]";

    return (
        <>
        {showSeparator && <span className="text-gray-300 dark:text-grayDark-600">•</span>}
        <div
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <button
                onClick={toggleBookmark}
                className={`${sizeClasses} flex items-center justify-center rounded-full transition-all duration-200 ${
                    isBookmarked
                        ? "bg-primary/10 text-primary hover:bg-primary/20"
                        : "bg-gray-100 dark:bg-grayDark-800 text-gray-500 dark:text-grayDark-400 hover:bg-gray-200 dark:hover:bg-grayDark-700 hover:text-gray-700 dark:hover:text-grayDark-300"
                } ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                disabled={isLoading}
                title={isBookmarked ? t("bookmark_remove") : t("bookmark_add")}
                aria-label={
                    isBookmarked ? t("bookmark_remove") : t("bookmark_add")
                }
            >
                <SolarIcon
                    name={isBookmarked ? "bookmark-bold" : "bookmark-linear"}
                    className={iconSizeClass}
                />
            </button>
        </div>
        </>
    );
}
